import React from 'react';
import { comparePensions } from '../calculations/pension';
import './Calculator.css';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Calculator Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="container">
          <div className="panel">
            <h2>Something went wrong</h2>
            <p><strong>Error:</strong> {this.state.error?.message || 'Unknown error occurred'}</p>
            <p><strong>Stack:</strong> {this.state.error?.stack || 'No stack trace available'}</p>
            <button 
              onClick={() => this.setState({ hasError: false, error: null })}
              className="print-button"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Function to determine tax code based on salary
const getDefaultTaxCode = (salary) => {
  try {
    if (salary <= 100000) return '1257L'; // Standard tax code for 2025-26
    if (salary <= 125140) return 'K' + Math.floor((salary - 100000) / 10); // Reduced personal allowance
    return '0T'; // No personal allowance for very high earners
  } catch (error) {
    console.error('Error in getDefaultTaxCode:', error);
    return '1257L'; // Fallback
  }
};

// Currency formatting
const formatCurrency = (amount) => {
  try {
    if (isNaN(amount) || !isFinite(amount)) {
      return '£0.00';
    }
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `£${amount.toFixed(2)}`;
  }
};

const SalaryInputPanel = ({ grossSalary, taxCode, onGrossSalaryChange, onTaxCodeChange }) => {
  const handleSalaryChange = (e) => {
    try {
      const value = Number(e.target.value);
      if (!isNaN(value) && value >= 0) {
        onGrossSalaryChange(value);
      }
    } catch (error) {
      console.error('Error changing salary:', error);
    }
  };

  const handleTaxCodeChange = (e) => {
    try {
      onTaxCodeChange(e.target.value);
    } catch (error) {
      console.error('Error changing tax code:', error);
    }
  };

  return (
    <div className="panel">
      <h2>Salary Information</h2>
      
      <div className="panel-content">
        <div className="form-group">
          <label htmlFor="gross-salary">Gross Basic Annual Salary</label>
          <input
            type="number"
            id="gross-salary"
            value={grossSalary || ''}
            onChange={handleSalaryChange}
            placeholder="Enter your gross basic annual salary"
            className="input"
            min="0"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="tax-code">Tax Code</label>
          <input
            type="text"
            id="tax-code"
            value={taxCode || ''}
            onChange={handleTaxCodeChange}
            placeholder="e.g. 1257L"
            className="input"
          />
          <div className="helper-text">
            Your tax code determines your personal allowance. Most employees use 1257L. Check your payslip if you have a different code.
          </div>
        </div>
        
        <div className="disclaimer">
          <strong>Important:</strong> This calculator assumes standard employment conditions and does not account for company benefits (car allowance, private medical, etc.), government benefits, student loans, or other factors that may affect your tax code. Results are for guidance only and should suit most standard employees. For complex situations, consult your payroll department or a tax advisor.
        </div>
      </div>
    </div>
  );
};

const CombinedComparisonPanel = ({ 
  currentEmployeePercentage, 
  currentEmployerPercentage, 
  onCurrentEmployeePercentageChange, 
  onCurrentEmployerPercentageChange,
  sacrificeEmployeePercentage, 
  sacrificeEmployerPercentage, 
  onSacrificeEmployeePercentageChange, 
  onSacrificeEmployerPercentageChange,
  currentCalculation,
  sacrificeCalculation
}) => {
  const [isCurrentEmployeeEditing, setIsCurrentEmployeeEditing] = React.useState(false);
  const [isCurrentEmployerEditing, setIsCurrentEmployerEditing] = React.useState(false);
  const [isSacrificeEmployeeEditing, setIsSacrificeEmployeeEditing] = React.useState(false);
  const [isSacrificeEmployerEditing, setIsSacrificeEmployerEditing] = React.useState(false);
  
  // Toggle states for monthly/annual view
  const [isCurrentAnnual, setIsCurrentAnnual] = React.useState(false);
  const [isSacrificeAnnual, setIsSacrificeAnnual] = React.useState(false);

  // Safe accessor function for calculation properties
  const safeGet = (calculation, property, defaultValue = 0) => {
    try {
      return calculation?.[property] ?? defaultValue;
    } catch (error) {
      console.error(`Error accessing ${property}:`, error);
      return defaultValue;
    }
  };

  const renderResultsSection = (calculation, isSalarySacrifice, title, isAnnual, toggleAnnual) => {
    if (!calculation) {
      return (
        <div className="loading">
          Enter salary and percentage values to see calculations
        </div>
      );
    }

    const multiplier = isAnnual ? 12 : 1;
    const periodLabel = isAnnual ? 'Annual' : 'Monthly';

    return (
      <div className="results">
        <div className="results-header">
          <h3>{periodLabel} Breakdown</h3>
          <div className="period-toggle">
            <label className="toggle-switch">
              <input 
                type="checkbox" 
                checked={isAnnual} 
                onChange={(e) => toggleAnnual(e.target.checked)}
              />
              <span className="toggle-slider">
                <span className="toggle-label monthly">M</span>
                <span className="toggle-label annual">A</span>
              </span>
            </label>
          </div>
        </div>
        
        <div className="result-item">
          <span className="label">Gross/contractual pay</span>
          <div className="value">
            {formatCurrency(safeGet(calculation, 'grossMonthlySalary') * multiplier)}
          </div>
        </div>

        <div className="result-item">
          <span className="label">{isSalarySacrifice ? 'Salary sacrifice amount' : 'Employee pension contribution'}</span>
          <div className="value">
            <div>{formatCurrency(safeGet(calculation, 'employeeContribution') * multiplier)}</div>
            {!isSalarySacrifice && safeGet(calculation, 'taxRelief') > 0 && safeGet(calculation, 'grossEmployeeContribution') > 0 && (
              <div className="value-secondary">
                (+ {formatCurrency(safeGet(calculation, 'taxRelief') * multiplier)} tax relief = {formatCurrency(safeGet(calculation, 'grossEmployeeContribution') * multiplier)} total)
              </div>
            )}
            {isSalarySacrifice && (
              <div className="value-secondary">&nbsp;</div>
            )}
          </div>
        </div>

        <div className="result-item">
          <span className="label">Taxable pay</span>
          <div className="value">
            {formatCurrency(safeGet(calculation, 'salaryAfterPension') * multiplier)}
          </div>
        </div>

        <div className="result-item">
          <span className="label">Income tax</span>
          <div className="value">
            {formatCurrency(safeGet(calculation, 'incomeTax') * multiplier)}
          </div>
        </div>

        <div className="result-item">
          <span className="label">Employee NI</span>
          <div className="value">
            {formatCurrency(safeGet(calculation, 'employeeNI') * multiplier)}
          </div>
        </div>

        <div className="result-item">
          <span className="label">Employer pension contribution</span>
          <div className="value">
            {formatCurrency(safeGet(calculation, 'employerContribution') * multiplier)}
          </div>
        </div>

        <div className="result-item highlight">
          <span className="label">Take-home pay</span>
          <div className="value">
            {formatCurrency(safeGet(calculation, 'takeHomePay') * multiplier)}
          </div>
        </div>

        <div className="result-item highlight">
          <span className="label">Total wealth (Take-home + EE + ER)</span>
          <div className="value">
            {formatCurrency(safeGet(calculation, 'totalWealth') * multiplier)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="panel comparison-panel">
      <div className="comparison-headers">
        <div className="comparison-header current-pension">
          <div className="comparison-header-left">
            <img 
              src="/Cyan.png" 
              alt="HG Construction" 
              className="brand-logo"
            />
          </div>
          <div className="comparison-header-center">
            <div className="header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2 12h20M6 8h12M6 16h12"/>
                <circle cx="4" cy="12" r="1.5" fill="currentColor"/>
                <circle cx="8" cy="12" r="1.5" fill="currentColor"/>
                <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                <circle cx="16" cy="12" r="1.5" fill="currentColor"/>
                <circle cx="20" cy="12" r="1.5" fill="currentColor"/>
              </svg>
            </div>
            <h2>Current Pension</h2>
          </div>
          <div className="comparison-header-right"></div>
        </div>
        <div className="comparison-header salary-sacrifice">
          <div className="comparison-header-left">
            <img 
              src="/HG logo blue.png" 
              alt="HG Construction" 
              className="brand-logo"
            />
          </div>
          <div className="comparison-header-center">
            <div className="header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
                <path d="M22 6h-6v2h4v4h2V6z"/>
              </svg>
            </div>
            <h2>Salary Sacrifice</h2>
          </div>
          <div className="comparison-header-right"></div>
        </div>
      </div>
      
      <div className="panel-content">
        <div className="comparison-grid">
          {/* Current Pension Side */}
          <div className="comparison-side">
            <h3>Input Details</h3>
            <div className="helper-text-center">
              (Double-click boxes to change contribution amounts)
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Employee Contribution %</label>
                <input
                  type="number"
                  value={currentEmployeePercentage}
                  onChange={(e) => onCurrentEmployeePercentageChange(Number(e.target.value))}
                  onDoubleClick={() => setIsCurrentEmployeeEditing(true)}
                  onBlur={() => setIsCurrentEmployeeEditing(false)}
                  onKeyPress={(e) => e.key === 'Enter' && setIsCurrentEmployeeEditing(false)}
                  min="0"
                  max="50"
                  step="0.1"
                  readOnly={!isCurrentEmployeeEditing}
                  className="input"
                  style={{
                    cursor: isCurrentEmployeeEditing ? 'text' : 'pointer',
                    backgroundColor: isCurrentEmployeeEditing ? 'white' : '#f3f2f1',
                    color: isCurrentEmployeeEditing ? '#323130' : '#8a8886'
                  }}
                />
                <div className="helper-text">
                  Default 5% (applied to qualified earnings £6,240 - £50,270)
                </div>
              </div>

              <div className="form-group">
                <label>Employer Contribution %</label>
                <input
                  type="number"
                  value={currentEmployerPercentage}
                  onChange={(e) => onCurrentEmployerPercentageChange(Number(e.target.value))}
                  onDoubleClick={() => setIsCurrentEmployerEditing(true)}
                  onBlur={() => setIsCurrentEmployerEditing(false)}
                  onKeyPress={(e) => e.key === 'Enter' && setIsCurrentEmployerEditing(false)}
                  min="0"
                  max="30"
                  step="0.1"
                  readOnly={!isCurrentEmployerEditing}
                  className="input"
                  style={{
                    cursor: isCurrentEmployerEditing ? 'text' : 'pointer',
                    backgroundColor: isCurrentEmployerEditing ? 'white' : '#f3f2f1',
                    color: isCurrentEmployerEditing ? '#323130' : '#8a8886'
                  }}
                />
                <div className="helper-text">
                  Default 3% (applied to qualified earnings £6,240 - £50,270)
                </div>
              </div>
            </div>

            {renderResultsSection(currentCalculation, false, 'Current Pension', isCurrentAnnual, setIsCurrentAnnual)}
          </div>

          {/* Salary Sacrifice Side */}
          <div className="comparison-side">
            <h3>Input Details</h3>
            <div className="helper-text-center">
              (Double-click boxes to change contribution amounts)
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Employee Sacrifice Amount %</label>
                <input
                  type="number"
                  value={sacrificeEmployeePercentage}
                  onChange={(e) => onSacrificeEmployeePercentageChange(Number(e.target.value))}
                  onDoubleClick={() => setIsSacrificeEmployeeEditing(true)}
                  onBlur={() => setIsSacrificeEmployeeEditing(false)}
                  onKeyPress={(e) => e.key === 'Enter' && setIsSacrificeEmployeeEditing(false)}
                  min="0"
                  max="50"
                  step="0.1"
                  readOnly={!isSacrificeEmployeeEditing}
                  className="input"
                  style={{
                    cursor: isSacrificeEmployeeEditing ? 'text' : 'pointer',
                    backgroundColor: isSacrificeEmployeeEditing ? 'white' : '#f3f2f1',
                    color: isSacrificeEmployeeEditing ? '#323130' : '#8a8886'
                  }}
                />
                <div className="helper-text">
                  Default 5% (applied to full salary for salary sacrifice)
                </div>
              </div>

              <div className="form-group">
                <label>Employer Contribution %</label>
                <input
                  type="number"
                  value={sacrificeEmployerPercentage}
                  onChange={(e) => onSacrificeEmployerPercentageChange(Number(e.target.value))}
                  onDoubleClick={() => setIsSacrificeEmployerEditing(true)}
                  onBlur={() => setIsSacrificeEmployerEditing(false)}
                  onKeyPress={(e) => e.key === 'Enter' && setIsSacrificeEmployerEditing(false)}
                  min="0"
                  max="30"
                  step="0.1"
                  readOnly={!isSacrificeEmployerEditing}
                  className="input"
                  style={{
                    cursor: isSacrificeEmployerEditing ? 'text' : 'pointer',
                    backgroundColor: isSacrificeEmployerEditing ? 'white' : '#f3f2f1',
                    color: isSacrificeEmployerEditing ? '#323130' : '#8a8886'
                  }}
                />
                <div className="helper-text">
                  Default 3% (applied to full salary for salary sacrifice)
                </div>
              </div>
            </div>

            {renderResultsSection(sacrificeCalculation, true, 'Salary Sacrifice', isSacrificeAnnual, setIsSacrificeAnnual)}
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryBar = ({ takeHomeDifference, wealthDifference }) => {
  // Safe number handling
  const safeTakeHomeDiff = isNaN(takeHomeDifference) ? 0 : takeHomeDifference;
  const safeWealthDiff = isNaN(wealthDifference) ? 0 : wealthDifference;

  return (
    <div className="summary">
      <div className="summary-grid">
        <div className="summary-item">
          <span>Monthly take-home pay change</span>
          <span className={`summary-value ${safeTakeHomeDiff >= 0 ? 'positive' : 'negative'}`}>
            {safeTakeHomeDiff >= 0 ? '+' : ''}{formatCurrency(safeTakeHomeDiff)}
          </span>
        </div>
        <div className="summary-item">
          <span>Monthly wealth change</span>
          <span className={`summary-value ${safeWealthDiff >= 0 ? 'positive' : 'negative'}`}>
            {safeWealthDiff >= 0 ? '+' : ''}{formatCurrency(safeWealthDiff)}
          </span>
        </div>
        <div className="summary-item">
          <span>Annual take-home pay change</span>
          <span className={`summary-value ${safeTakeHomeDiff >= 0 ? 'positive' : 'negative'}`}>
            {safeTakeHomeDiff >= 0 ? '+' : ''}{formatCurrency(safeTakeHomeDiff * 12)}
          </span>
        </div>
        <div className="summary-item">
          <span>Annual wealth change</span>
          <span className={`summary-value ${safeWealthDiff >= 0 ? 'positive' : 'negative'}`}>
            {safeWealthDiff >= 0 ? '+' : ''}{formatCurrency(safeWealthDiff * 12)}
          </span>
        </div>
      </div>
      <div className="blurb">
        Most people gain overall because National Insurance & tax savings boost pension contributions.
      </div>
    </div>
  );
};

export const Calculator = ({ title }) => {
  const [salary, setSalary] = React.useState(50000);
  const [taxCode, setTaxCode] = React.useState('1257L');
  const [currentEmployeePercentage, setCurrentEmployeePercentage] = React.useState(5);
  const [currentEmployerPercentage, setCurrentEmployerPercentage] = React.useState(3);
  const [sacrificeEmployeePercentage, setSacrificeEmployeePercentage] = React.useState(5);
  const [sacrificeEmployerPercentage, setSacrificeEmployerPercentage] = React.useState(3);
  const [error, setError] = React.useState(null);

  // Auto-update tax code when salary changes
  const handleSalaryChange = (newSalary) => {
    try {
      setSalary(newSalary);
      setTaxCode(getDefaultTaxCode(newSalary));
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error in handleSalaryChange:', err);
      setError(`Error updating salary: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const comparison = React.useMemo(() => {
    try {
      if (salary > 0) {
        const result = comparePensions(
          salary,
          currentEmployeePercentage,
          currentEmployerPercentage,
          sacrificeEmployeePercentage,
          sacrificeEmployerPercentage,
          taxCode
        );
        setError(null); // Clear any previous errors
        return result;
      }
      return null;
    } catch (err) {
      console.error('Error in pension comparison:', err);
      setError(`Calculation error: ${err instanceof Error ? err.message : 'Unknown error'}`);
      return null;
    }
  }, [salary, taxCode, currentEmployeePercentage, currentEmployerPercentage, sacrificeEmployeePercentage, sacrificeEmployerPercentage]);

  const handlePrint = () => {
    try {
      window.print();
    } catch (err) {
      console.error('Error printing:', err);
      setError('Printing not available in this environment');
    }
  };

  return (
    <ErrorBoundary>
      <div className="container">
        {error && (
          <div className="panel" style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', color: '#721c24' }}>
            <h3>Error</h3>
            <p>{error}</p>
            <button onClick={() => setError(null)} className="print-button">
              Clear Error
            </button>
          </div>
        )}
        
        <div className="integrated-panel">
          <div className="panel-header">
            <div className="logo-area">
              <img 
                src="/Cyan.png" 
                alt="HG Construction" 
                className="company-logo"
              />
            </div>
            <div className="title-section">
              <h1>{title || 'Salary Sacrifice Comparator'}</h1>
              <p>Compare your current pension with salary sacrifice options</p>
            </div>
            <div className="header-actions">
              <button className="print-button" onClick={handlePrint}>
                🖨️ Print/Save PDF
              </button>
            </div>
          </div>
          
          <div className="developer-credit-header">
            Developed by the HG IT Department.
          </div>
          
          <div className="salary-inputs">
            <div className="form-group">
              <label htmlFor="gross-salary">Gross Basic Annual Salary</label>
              <input
                type="number"
                id="gross-salary"
                value={salary || ''}
                onChange={(e) => {
                  try {
                    const value = Number(e.target.value);
                    if (!isNaN(value) && value >= 0) {
                      handleSalaryChange(value);
                    }
                  } catch (error) {
                    console.error('Error changing salary:', error);
                  }
                }}
                placeholder="Enter your gross basic annual salary"
                className="input"
                min="0"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="tax-code">Tax Code</label>
              <input
                type="text"
                id="tax-code"
                value={taxCode || ''}
                onChange={(e) => {
                  try {
                    setTaxCode(e.target.value);
                  } catch (error) {
                    console.error('Error changing tax code:', error);
                  }
                }}
                placeholder="e.g. 1257L"
                className="input"
              />
              <div className="helper-text">
                Your tax code determines your personal allowance. Most employees use 1257L. Check your payslip if you have a different code.
              </div>
            </div>
            
            <div className="disclaimer">
              <strong>Important:</strong> This calculator assumes standard employment conditions and does not account for company benefits (car allowance, private medical, etc.), government benefits, student loans, or other factors that may affect your tax code. Results are for guidance only and should suit most standard employees. For complex situations, consult your payroll department or a tax advisor.
            </div>
          </div>
        </div>

        <CombinedComparisonPanel
          currentEmployeePercentage={currentEmployeePercentage}
          currentEmployerPercentage={currentEmployerPercentage}
          onCurrentEmployeePercentageChange={setCurrentEmployeePercentage}
          onCurrentEmployerPercentageChange={setCurrentEmployerPercentage}
          sacrificeEmployeePercentage={sacrificeEmployeePercentage}
          sacrificeEmployerPercentage={sacrificeEmployerPercentage}
          onSacrificeEmployeePercentageChange={setSacrificeEmployeePercentage}
          onSacrificeEmployerPercentageChange={setSacrificeEmployerPercentage}
          currentCalculation={comparison?.current}
          sacrificeCalculation={comparison?.salarySacrifice}
        />

        {comparison && (
          <SummaryBar
            takeHomeDifference={comparison.takeHomeDifference}
            wealthDifference={comparison.wealthDifference}
          />
        )}

        <div className="disclaimer">
          <p>
            <strong>Important:</strong> Traditional pension contributions are calculated on qualified earnings (£6,240 - £50,270 per annum). 
            Salary sacrifice contributions can be applied to the full salary, which often makes them more beneficial.
          </p>
          <p>
            Illustration only; excludes bonuses, benefits-in-kind, or state benefits. 
            Actual take-home may differ. No data is stored or transmitted.
          </p>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default Calculator;